// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { DynProperty, DynPropertyRelationType } from '../../../DynProperty';
import { Color } from '../../../Graphics';
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterBoolean } from '../InputParamaterClasses/InputParameterBoolean';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
import { InputParameterDouble } from '../InputParamaterClasses/InputParameterDouble';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
const MA_TYPE_PROPERTY_NAME = 'Type of moving average';
const UPPER_DEVIATION_PROPERTY_NAME = 'Upper deviation (%)';
const LOWER_DEVIATION_PROPERTY_NAME = 'Lower deviation (%)';
const BAR_FILTER_PROPERTY_NAME = 'Bar filter';
const ATR_DEVIATION_VALUE_NAME = 'ATR Deviation-based';
const MMPC_VALUE_NAME = 'Maximum Movement from Previous Close';
const LINE_VISIBLE_PROPERTY_NAME = 'Visible 0';
const LINE_DRAW_PLATE_PROPERTY_NAME = 'Draw Plate 0';
const DEVIATION_TOOLTIP = 'ATR based on filtered High–Low ranges with deviation limits.';
const ATR_DEVIATION_COLOR = '#2962FF';
const MMPC_COLOR = '#089981';
export class ATR extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Average True Range';
        this.Comments = 'Measures market volatility';
        super.SetIndicatorLine('Line', Color.Blue, 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.MAPeriod = 13;
        super.InputParameter(new InputParameterInteger('MAPeriod', 'Period of Moving Average', 0, 1, 9999));
        this.MAType = MAMode.SMA;
        super.InputParameter(new InputParameterCombobox('MAType', MA_TYPE_PROPERTY_NAME, 1, [
            { Simple: MAMode.SMA },
            { Exponential: MAMode.EMA },
            { Modified: MAMode.SMMA },
            { 'Linear Weighted': MAMode.LWMA },
            { 'Deviation-based': MAMode.DeviationBased }
        ]));
        this.UpperDeviation = 150;
        super.InputParameter(new InputParameterDouble('UpperDeviation', UPPER_DEVIATION_PROPERTY_NAME, 2, 100, 999999, 0, 1));
        this.LowerDeviation = 50;
        super.InputParameter(new InputParameterDouble('LowerDeviation', LOWER_DEVIATION_PROPERTY_NAME, 3, 1, 999999, 0, 1));
        this.BarFilter = true;
        super.InputParameter(new InputParameterBoolean('BarFilter', BAR_FILTER_PROPERTY_NAME, 4));
        this.ATRDeviationColor = ATR_DEVIATION_COLOR;
        this.MMPCColor = MMPC_COLOR;
        super.SetValueEntry(ATR_DEVIATION_VALUE_NAME, Number.MAX_VALUE, this.ATRDeviationColor);
        super.SetValueEntry(MMPC_VALUE_NAME, Number.MAX_VALUE, this.MMPCColor);
        this.ApplyDeviationBasedMode();
    }
    Init() {
        this.IndicatorShortName('ATR(' + this.MAPeriod + ')');
    }
    GetIndicatorShortName() {
        return 'ATR(' + this.MAPeriod + ')';
    }
    getCustomName() {
        return 'ATR (' + this.ProjectName + ')';
    }
    getMinBarsCount() {
        return this.IsDeviationBased() ? this.MAPeriod + 1 : this.MAPeriod;
    }
    Properties() {
        const props = super.Properties().slice();
        const isDeviationBased = this.IsDeviationBased();
        const maTypeProp = DynProperty.getPropertyByName(props, MA_TYPE_PROPERTY_NAME);
        if (!isNullOrUndefined(maTypeProp)) {
            maTypeProp.assignedProperty = [
                UPPER_DEVIATION_PROPERTY_NAME,
                LOWER_DEVIATION_PROPERTY_NAME,
                BAR_FILTER_PROPERTY_NAME,
                ATR_DEVIATION_VALUE_NAME,
                MMPC_VALUE_NAME
            ];
            maTypeProp.DynPropertyRelationType = DynPropertyRelationType.Visibility;
            maTypeProp.tooltip = DEVIATION_TOOLTIP;
            if (Array.isArray(maTypeProp.objectVariants)) {
                const deviationItem = maTypeProp.objectVariants.find((item) => item.value === MAMode.DeviationBased);
                if (!isNullOrUndefined(deviationItem)) {
                    deviationItem.tooltip = DEVIATION_TOOLTIP;
                }
            }
        }
        this.PrepareDeviationProperty(props, UPPER_DEVIATION_PROPERTY_NAME, isDeviationBased, DEVIATION_TOOLTIP);
        this.PrepareDeviationProperty(props, LOWER_DEVIATION_PROPERTY_NAME, isDeviationBased, DEVIATION_TOOLTIP);
        this.PrepareDeviationProperty(props, BAR_FILTER_PROPERTY_NAME, isDeviationBased, DEVIATION_TOOLTIP);
        props.push(this.CreateValueColorProperty(ATR_DEVIATION_VALUE_NAME, this.ATRDeviationColor, 5, isDeviationBased, DEVIATION_TOOLTIP));
        props.push(this.CreateValueColorProperty(MMPC_VALUE_NAME, this.MMPCColor, 6, isDeviationBased, 'Maximum price movement from previous close, expressed as a percentage of ATR.'));
        return props;
    }
    callBack(props) {
        const needRecalculate = super.callBack(props);
        const atrDeviationColorProp = DynProperty.getPropertyByName(props, ATR_DEVIATION_VALUE_NAME);
        if (!isNullOrUndefined(atrDeviationColorProp)) {
            this.ATRDeviationColor = atrDeviationColorProp.value;
        }
        const mmpcColorProp = DynProperty.getPropertyByName(props, MMPC_VALUE_NAME);
        if (!isNullOrUndefined(mmpcColorProp)) {
            this.MMPCColor = mmpcColorProp.value;
        }
        this.ApplyLineProperties(props);
        this.ApplyDeviationBasedMode();
        return needRecalculate;
    }
    OnQuote() {
        if (this.IsDeviationBased()) {
            this.CalculateDeviationBased();
            return;
        }
        this.SetDeviationValuesVisibility(false);
        if (this.CurrentData.Count < this.MAPeriod) {
            return;
        }
        switch (this.MAType) {
            case MAMode.SMA:
                this.SetValue(0, 0, this.GetSMA());
                break;
            case MAMode.EMA:
                this.SetValue(0, 0, this.GetEMA());
                break;
            case MAMode.SMMA:
                this.SetValue(0, 0, this.GetMMA());
                break;
            case MAMode.LWMA:
                this.SetValue(0, 0, this.GetLWMA());
                break;
        }
    }
    GetTrueRange(index) {
        const CurrentData = this.CurrentData;
        const hi = CurrentData.getPriceByType(PriceType.High, index);
        const lo = CurrentData.getPriceByType(PriceType.Low, index);
        const prevClose = CurrentData.Count > index + 1
            ? CurrentData.getPriceByType(PriceType.Close, index + 1)
            : CurrentData.getPriceByType(PriceType.Close, index);
        return Math.max(hi - lo, Math.max(Math.abs(prevClose - hi), Math.abs(prevClose - lo)));
    }
    GetSMA() {
        let summa = 0; // Sum of prices
        // Loop of calculation.
        for (let i = 0; i < this.MAPeriod; i++) {
            summa += this.GetTrueRange(i);
        }
        // Returning current value of the SMA
        return summa / this.MAPeriod;
    }
    GetEMA() {
        // Calculation of a coefficient
        const k = 2 / (this.MAPeriod + 1);
        // Sum of prices
        let summa = this.GetTrueRange(this.MAPeriod - 1);
        for (let i = this.MAPeriod - 2; i >= 0; i--) {
            summa += k * (this.GetTrueRange(i) - summa);
        }
        // Returning value
        return summa;
    }
    GetMMA() {
        // Current price
        let mma = this.GetTrueRange(0);
        // coefficient
        const k = 1 / this.MAPeriod;
        // Loop of calculation.
        for (let i = 0; i < this.MAPeriod && mma > 0; i++) {
            mma = this.GetTrueRange(i) * k + mma * (1 - k);
        }
        // Returning of mma
        return mma;
    }
    GetLWMA() {
        // Numerator of the rate
        let numerator = 0;
        // Denominator of the rate
        let denominator = 0;
        // period counter
        let period = this.MAPeriod;
        // Loop of calculation.
        for (let i = 0; i < this.MAPeriod; i++) {
            numerator += period * this.GetTrueRange(i);
            denominator += period;
            period--;
        }
        return denominator === 0 ? 0 : numerator / denominator;
    }
    CalculateDeviationBased() {
        this.SetValue(0, 0, Number.MAX_VALUE);
        this.SetDeviationValuesVisibility(true);
        if (this.CurrentData.Count < this.MAPeriod + 1) {
            this.SetDeviationValuesInvalid();
            return;
        }
        const atr = this.GetDeviationBasedATR();
        if (!this.IsValidFiniteNumber(atr) || atr <= 0) {
            this.SetDeviationValuesInvalid();
            return;
        }
        const mmpc = this.GetMMPC(atr);
        const roundedMmpc = Math.round(mmpc * 100) / 100;
        this.SetValueEntryValue(ATR_DEVIATION_VALUE_NAME, atr);
        this.SetValueEntryValue(MMPC_VALUE_NAME, this.IsValidFiniteNumber(roundedMmpc) ? roundedMmpc : Number.MAX_VALUE);
    }
    GetDeviationBasedATR() {
        const ranges = [];
        for (let i = 1; i <= this.MAPeriod; i++) {
            const range = this.GetBaseRange(i);
            if (!this.IsValidFiniteNumber(range) || range <= 0) {
                return Number.NaN;
            }
            ranges.push(range);
        }
        const baselineATR = this.GetAverage(ranges);
        if (!this.IsValidFiniteNumber(baselineATR) || baselineATR <= 0) {
            return Number.NaN;
        }
        if (!this.BarFilter) {
            return baselineATR;
        }
        const upperLimit = baselineATR * (this.UpperDeviation / 100);
        const lowerLimit = baselineATR * (this.LowerDeviation / 100);
        const isValid = ranges.map((range) => range >= lowerLimit && range <= upperLimit);
        if (!isValid[0]) {
            return Number.NaN;
        }
        let newestPreviousValidRange = ranges[0];
        let sum = 0;
        for (let i = 0; i < ranges.length; i++) {
            if (isValid[i]) {
                newestPreviousValidRange = ranges[i];
                sum += ranges[i];
            }
            else {
                sum += newestPreviousValidRange;
            }
        }
        return sum / this.MAPeriod;
    }
    GetBaseRange(index) {
        const high = this.CurrentData.getPriceByType(PriceType.High, index);
        const low = this.CurrentData.getPriceByType(PriceType.Low, index);
        return high - low;
    }
    GetMMPC(atr) {
        if (!this.IsValidFiniteNumber(atr) || atr <= 0 || this.CurrentData.Count < 2) {
            return Number.NaN;
        }
        const close = this.CurrentData.getPriceByType(PriceType.Close, 0);
        const prevClose = this.CurrentData.getPriceByType(PriceType.Close, 1);
        if (!this.IsValidFiniteNumber(close) || !this.IsValidFiniteNumber(prevClose)) {
            return Number.NaN;
        }
        return (Math.abs(close - prevClose) / atr) * 100;
    }
    GetAverage(values) {
        if (values.length === 0) {
            return Number.NaN;
        }
        let sum = 0;
        for (const value of values) {
            sum += value;
        }
        return sum / values.length;
    }
    SetDeviationValuesInvalid() {
        this.SetValueEntryValue(ATR_DEVIATION_VALUE_NAME, Number.MAX_VALUE);
        this.SetValueEntryValue(MMPC_VALUE_NAME, Number.MAX_VALUE);
    }
    ApplyValueEntrySettings() {
        const atrDeviationValueEntry = this.GetValueEntry(ATR_DEVIATION_VALUE_NAME);
        if (!isNullOrUndefined(atrDeviationValueEntry)) {
            atrDeviationValueEntry.Color = this.ATRDeviationColor;
        }
        const mmpcValueEntry = this.GetValueEntry(MMPC_VALUE_NAME);
        if (!isNullOrUndefined(mmpcValueEntry)) {
            mmpcValueEntry.Color = this.MMPCColor;
        }
        this.SetDeviationValuesVisibility(this.IsDeviationBased());
    }
    ApplyDeviationBasedMode() {
        this.SeparateWindow = !this.IsDeviationBased();
        this.ApplyValueEntrySettings();
    }
    SetDeviationValuesVisibility(visible) {
        const atrDeviationValueEntry = this.GetValueEntry(ATR_DEVIATION_VALUE_NAME);
        if (!isNullOrUndefined(atrDeviationValueEntry)) {
            atrDeviationValueEntry.Visible = visible;
        }
        const mmpcValueEntry = this.GetValueEntry(MMPC_VALUE_NAME);
        if (!isNullOrUndefined(mmpcValueEntry)) {
            mmpcValueEntry.Visible = visible;
        }
    }
    PrepareDeviationProperty(props, propertyName, visible, tooltip) {
        const prop = DynProperty.getPropertyByName(props, propertyName);
        if (isNullOrUndefined(prop)) {
            return;
        }
        prop.visible = visible;
        prop.enabilityValue = MAMode.DeviationBased;
        prop.tooltip = tooltip;
        prop.propertyComment = propertyName;
    }
    CreateValueColorProperty(name, value, sortIndex, visible, tooltip) {
        const prop = new DynProperty(name, value, DynProperty.COLOR, DynProperty.SCRIPT_PARAMETERS_GROUP);
        prop.sortIndex = sortIndex;
        prop.visible = visible;
        prop.enabilityValue = MAMode.DeviationBased;
        prop.tooltip = tooltip;
        prop.propertyComment = name;
        return prop;
    }
    ApplyLineProperties(props) {
        const isDeviationBased = this.IsDeviationBased();
        const visibleProp = DynProperty.getPropertyByName(props, LINE_VISIBLE_PROPERTY_NAME);
        if (!isNullOrUndefined(visibleProp)) {
            visibleProp.value = !isDeviationBased;
            visibleProp.enabled = !isDeviationBased;
        }
        const drawPlateProp = DynProperty.getPropertyByName(props, LINE_DRAW_PLATE_PROPERTY_NAME);
        if (!isNullOrUndefined(drawPlateProp)) {
            drawPlateProp.value = !isDeviationBased;
            drawPlateProp.enabled = !isDeviationBased;
        }
    }
    IsDeviationBased() {
        return this.MAType === MAMode.DeviationBased;
    }
    IsValidFiniteNumber(value) {
        return isValidNumber(value) && Number.isFinite(value);
    }
}
